import React, { useEffect, useState } from 'react';
import { useDispatch, Provider } from 'react-redux';
import { logout, setUser } from './userSlice';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorPage from './ErrorPage';
import Login from './login/Login';
import Dashboard from './dashboard/Dashboard';
import { store } from './store';
import './index.css';
import Users from './users/Users';
import Publications from './publications/Publications';
import PublicationForm from './publications/form/PublicationForm';
import ArchivedPublications from './publications/archived/ArchivedPublications';
import './service/axiosConfig';
import PrivateRoute from './admin/PrivateRoute';
import UserRoute from './UserRoute';
import SsoLogin from './ssologin/SsoLogin';
import DashboardSso from './dashboardsso/DashboardSso';
import DashboardAdmin from './dashboardadmin/AdminComponents/DashboardAdmin';
import Reviews from './reviews/Reviews';
import ShareReviewPage from './reviews/share/ShareReviewPage';

const AppWithUserPersistence = () => {
    const dispatch = useDispatch();
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        const initializeAuth = () => {
            const userEmail = localStorage.getItem('userEmail') || '';
            const agencyName = localStorage.getItem('agencyName') || '';
            const userId = localStorage.getItem('userId')
                ? Number(localStorage.getItem('userId'))
                : null;
            const userRoles = localStorage.getItem('roles');
            const token = localStorage.getItem('userToken');
            const isAdmin = userRoles?.includes('ROLE_ADMIN');
            const quota = Number(localStorage.getItem('quota'));
            const isSsoUser = localStorage.getItem('isSsoUser') === 'true';
            const userMyId = localStorage.getItem('userMyId') || '';
            const groupement = JSON.parse(localStorage.getItem('groupement')!) || null;
            const autoPublish = localStorage.getItem('autoPublish') === 'true';

            if (userEmail && userId !== null && token) {
                dispatch(
                    setUser({
                        isAuthenticated: true,
                        isAdmin: !!isAdmin,
                        id: userId,
                        email: userEmail,
                        agencyName: agencyName,
                        token: token,
                        quota: quota,
                        role: userRoles,
                        isSsoUser: isSsoUser,
                        userMyId: userMyId,
                        groupement: groupement,
                        autoPublish: autoPublish,
                    }),
                );
            } else {
                dispatch(logout());
            }
            setInitialized(true);
        };

        initializeAuth();
    }, [dispatch]);

    if (!initialized) {
        return <div>Loading...</div>;
    }

    if (localStorage.getItem('countUnreadReview')) {
        localStorage.removeItem('countUnreadReview');
    }

    const router = createBrowserRouter([
        {
            path: '/',
            element: <App />,
            errorElement: <ErrorPage />,
            children: [
                {
                    path: 'login',
                    element: <Login />,
                },
                {
                    path: 'sso-login',
                    element: <SsoLogin />,
                },
                {
                    path: '/',
                    element: <UserRoute />,
                    children: [
                        {
                            path: 'dashboard',
                            element: <Dashboard />,
                        },
                        {
                            path: 'dashboard_agency',
                            element: <DashboardSso />,
                        },
                        {
                            path: 'dashboard_admin',
                            element: <DashboardAdmin />,
                        },
                        {
                            path: 'users',
                            element: <Users />,
                        },
                        {
                            path: 'publications',
                            element: <Publications />,
                        },
                        {
                            path: 'add_publication',
                            element: <PublicationForm />,
                        },
                        {
                            path: `edit_publication/:publicationId`,
                            element: <PublicationForm />,
                        },
                        {
                            path: `show_publication/:publicationId`,
                            element: <PublicationForm />,
                        },
                        {
                            path: 'archived_publications',
                            element: <ArchivedPublications />,
                        },
                        {
                            path: 'reviews',
                            element: <Reviews />,
                        },
                        {
                            path: 'share-review/:reviewId',
                            element: <ShareReviewPage />,
                        },
                    ],
                },
                {
                    path: 'administration',
                    element: <PrivateRoute />,
                },
            ],
        },
    ]);

    return (
        <React.StrictMode>
            <RouterProvider router={router} />
        </React.StrictMode>
    );
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Provider store={store}>
            <AppWithUserPersistence />
        </Provider>
    </React.StrictMode>,
);
